import './index.scss';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import PhotoSwipe from 'photoswipe';
import 'photoswipe/style.css';

const PSWP_CLICK_ZOOM_MODE = new WeakMap();

/**
 * Secure Viewer (WEST-703): single-page image viewer with prev/next, zoom (including pinch),
 * and client-side protections (no context menu, no print, overlay to hinder drag-to-save).
 * Uses session cookie; fetches /api/secure-viewer/page-token?page=N then /api/secure-viewer/page/N?h=...
 * @see wf-website-docs/features/WEST-703-frontend-viewer-and-ux/implementation-plan.md
 */

const ZOOM_STEPS = [1, 1.25, 1.5, 2];

/**
 * Token hygiene helper:
 * If `token` is present in the initial URL, remove it via `history.replaceState`
 * so the token doesn't stick around in the browser address bar / referrers.
 */
function removeTokenFromUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        if (!token) return;

        params.delete('token');
        const newQuery = params.toString();
        const nextUrl = window.location.pathname + (newQuery ? `?${newQuery}` : '') + window.location.hash;
        window.history.replaceState({}, '', nextUrl);
    } catch (_) {
        // If localStorage is blocked or any parsing fails, just proceed normally.
    }
}

/**
 * @param {Element} shell
 * @param {string} key
 * @returns {string}
 */
function getMessage(shell, key) {
    const attr = shell.getAttribute(`data-secure-viewer-msg-${key}`);
    return typeof attr === 'string' && attr.length > 0 ? attr : '';
}

/**
 * @param {Element} shell
 * @param {number} pageNumber
 * @param {{ prefetch?: boolean }} [options]
 * @returns {Promise<{ blobUrl: string, img: HTMLImageElement }>}
 */
function loadPageImage(shell, pageNumber, options = {}) {
    const prefetch = options && options.prefetch === true;
    const extra = prefetch ? '&pf=1' : '';
    return fetch(`/api/secure-viewer/page-token?page=${pageNumber}${extra}`, {
        credentials: 'same-origin',
    })
        .then((res) => {
            if (!res.ok) {
                const err = new Error(`page-token ${res.status}`);
                err.status = res.status;
                return res.json().then((body) => {
                    err.body = body;
                    throw err;
                }).catch((parseErr) => {
                    throw err;
                });
            }
            return res.json();
        })
        .then((data) => {
            if (!data || typeof data.h !== 'string') throw new Error('No hash in page-token response');
            const h = data.h;
            return fetch(`/api/secure-viewer/page/${pageNumber}?h=${encodeURIComponent(h)}${prefetch ? '&pf=1' : ''}`, {
                credentials: 'same-origin',
            });
        })
        .then((res) => {
            if (!res.ok) {
                const err = new Error(`page ${res.status}`);
                err.status = res.status;
                return res.json().then((body) => {
                    err.body = body;
                    throw err;
                }).catch(() => {
                    throw err;
                });
            }
            return res.blob();
        })
        .then((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            const img = document.createElement('img');
            img.src = blobUrl;
            img.alt = `Page ${pageNumber}`;
            img.className = 'secure-viewer__page-image';
            img.loading = 'lazy';
            // Fire-and-forget decode:
            // - We must not block rendering on `decode()` because some browsers
            //   may take a long time or never resolve for blob-backed images,
            //   which would otherwise lead to endless "Laden..." state.
            img.decode().catch(() => {});
            return { blobUrl, img };
        });
}

/**
 * @param {Element} shell
 * @param {number} status
 * @param {Object} [body] - optional JSON body for error code (e.g. code_redeemed)
 * @returns {string}
 */
function messageForStatus(shell, status, body) {
    if (status === 401) return getMessage(shell, 'session-expired');
    if (status === 403) {
        if (body && body.error === 'code_redeemed') return getMessage(shell, 'code-redeemed');
        return getMessage(shell, 'access-denied');
    }
    if (status === 404) return getMessage(shell, 'page-not-found');
    if (status >= 500) return getMessage(shell, 'server-error');
    return getMessage(shell, 'server-error');
}

/**
 * @param {Element} shell
 * @param {number} currentPage
 * @param {number} totalPages
 */
function updatePageIndicator(shell, currentPage, totalPages) {
    const indicator = shell.querySelector('.secure-viewer__page-indicator');
    if (indicator) {
        indicator.textContent = `${currentPage} / ${totalPages}`;
        indicator.setAttribute('aria-label', `Page ${currentPage} of ${totalPages}`);
    }
}

/**
 * @param {Element} shell
 * @param {number} currentPage
 * @param {number} totalPages
 */
function updateNavButtons(shell, currentPage, totalPages) {
    const prevBtn = shell.querySelector('.secure-viewer__btn--prev');
    const nextBtn = shell.querySelector('.secure-viewer__btn--next');
    if (prevBtn) prevBtn.disabled = currentPage <= 1;
    if (nextBtn) nextBtn.disabled = currentPage >= totalPages;
}

/**
 * @param {Element} container
 * @param {number} scale
 */
function applyZoom(container, scale) {
    const wrap = container.querySelector('.secure-viewer__page-image-wrap');
    if (wrap) wrap.style.transform = `scale(${scale})`;
}

/**
 * @param {Element} shell
 * @returns {{ viewArea: Element, pageContainer: Element, currentPage: number, totalPages: number, zoomIndex: number, cache: Map<number, { blobUrl: string, img: HTMLImageElement }>, currentBlobUrl: string | null }}
 */
function getState(shell) {
    if (!shell._secureViewerState) {
        const totalPages = parseInt(shell.getAttribute('data-secure-viewer-total-pages'), 10);
        shell._secureViewerState = {
            viewArea: shell.querySelector('.secure-viewer__view-area'),
            pageContainer: shell.querySelector('.secure-viewer__page-container'),
            currentPage: 1,
            totalPages: Number.isFinite(totalPages) ? totalPages : 0,
            zoomIndex: 0,
            cache: new Map(),
            currentBlobUrl: null,
            photoSwipeLightbox: null,
            photoSwipeGalleryEl: null,
            photoSwipeBlobUrls: new Map(),
            photoSwipePagePromises: new Map(),
            photoSwipeZoomMode: 'fit', // 'fit' | 'width' | 'custom'
            photoSwipeLastCustomZoomLevel: null,
            photoSwipeUiEls: null,
            photoSwipeSuppressAutoCustomUntil: 0,
            /** False until first `openingAnimationEnd` so zoom events during open don't flip mode. */
            photoSwipeUiReady: false,
        };
    }
    return shell._secureViewerState;
}

/**
 * @param {Element} shell
 * @param {string} htmlOrText
 * @param {boolean} [isError]
 */
function setContainerContent(shell, htmlOrText, isError) {
    const state = getState(shell);
    const container = state.pageContainer;
    if (!container) return;
    container.setAttribute('aria-busy', 'false');
    container.innerHTML = '';
    const p = document.createElement(isError ? 'div' : 'p');
    p.className = isError ? 'secure-viewer__page-error' : 'secure-viewer__placeholder';
    if (isError) p.setAttribute('role', 'alert');
    if (htmlOrText.startsWith('<')) {
        p.innerHTML = htmlOrText;
    } else {
        p.textContent = htmlOrText;
    }
    container.appendChild(p);
}

/**
 * @param {Element} shell
 * @param {number} status
 * @param {Object} [body]
 */
function showError(shell, status, body) {
    if (status === 401) {
        // Session expired during runtime AJAX: force a full page reload once.
        // Server-side logic then decides whether to render AG form or an error state.
        if (!window.__secureViewerSessionReloadTriggered) {
            window.__secureViewerSessionReloadTriggered = true;
            window.location.reload();
            return;
        }
    }

    const msg = messageForStatus(shell, status, body);
    const state = getState(shell);
    const container = state.pageContainer;
    if (!container) return;
    container.setAttribute('aria-busy', 'false');
    container.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'secure-viewer__page-error';
    wrap.setAttribute('role', 'alert');
    wrap.textContent = msg;
    if (status === 401) wrap.classList.add('secure-viewer__session-expired');

    container.appendChild(wrap);
    updateNavButtons(shell, state.currentPage, state.totalPages);
}

/**
 * Revoke a blob URL and remove from cache if present.
 * @param {Element} shell
 * @param {string} blobUrl
 */
function revokeBlobUrl(shell, blobUrl) {
    if (!blobUrl) return;
    try {
        URL.revokeObjectURL(blobUrl);
    } catch (_) {}
    const state = getState(shell);
    for (const [page, entry] of state.cache) {
        if (entry.blobUrl === blobUrl) {
            state.cache.delete(page);
            break;
        }
    }
}

/**
 * Prefetch adjacent page into cache (fire-and-forget).
 * @param {Element} shell
 * @param {number} pageNumber
 * @param {number} totalPages
 */
function prefetchAdjacent(shell, pageNumber, totalPages) {
    const state = getState(shell);
    for (const p of [pageNumber - 1, pageNumber + 1]) {
        if (p < 1 || p > totalPages || state.cache.has(p)) continue;
        loadPageImage(shell, p, { prefetch: true })
            .then(({ blobUrl, img }) => {
                state.cache.set(p, { blobUrl, img });
            })
            .catch(() => {});
    }
}

/**
 * Show a specific page (from cache or fetch).
 * @param {Element} shell
 * @param {number} pageNumber
 */
function goToPage(shell, pageNumber) {
    const state = getState(shell);
    const { totalPages, pageContainer } = state;
    if (pageNumber < 1 || pageNumber > totalPages) return;

    state.currentPage = pageNumber;
    updatePageIndicator(shell, pageNumber, totalPages);
    updateNavButtons(shell, pageNumber, totalPages);

    pageContainer.setAttribute('aria-busy', 'true');
    setContainerContent(shell, getMessage(shell, 'loading'), false);

    const cached = state.cache.get(pageNumber);
    if (cached) {
        if (state.currentBlobUrl) revokeBlobUrl(shell, state.currentBlobUrl);
        state.currentBlobUrl = null;
        renderPageImage(shell, cached.img, pageNumber);
        pageContainer.setAttribute('aria-busy', 'false');
        prefetchAdjacent(shell, pageNumber, totalPages);
        return;
    }

    loadPageImage(shell, pageNumber)
        .then(({ blobUrl, img }) => {
            if (state.currentPage !== pageNumber) {
                revokeBlobUrl(shell, blobUrl);
                return;
            }
            if (state.currentBlobUrl) revokeBlobUrl(shell, state.currentBlobUrl);
            state.currentBlobUrl = blobUrl;
            state.cache.set(pageNumber, { blobUrl, img });
            renderPageImage(shell, img, pageNumber);
            pageContainer.setAttribute('aria-busy', 'false');
            prefetchAdjacent(shell, pageNumber, totalPages);
        })
        .catch((err) => {
            if (state.currentPage !== pageNumber) return;
            const status = err && typeof err.status === 'number' ? err.status : 0;
            let body;
            try {
                if (err && err.body) body = err.body;
            } catch (_) {}
            showError(shell, status, body);
        });
}

/**
 * @param {Element} shell
 * @param {HTMLImageElement} img
 * @param {number} pageNumber
 */
function renderPageImage(shell, img, pageNumber) {
    const state = getState(shell);
    const { pageContainer } = state;
    pageContainer.innerHTML = '';
    const wrap = document.createElement('div');
    wrap.className = 'secure-viewer__page-image-wrap';
    const scale = ZOOM_STEPS[state.zoomIndex] || 1;
    wrap.style.transform = `scale(${scale})`;
    img.alt = `Page ${pageNumber} of ${state.totalPages}`;
    wrap.appendChild(img);
    pageContainer.appendChild(wrap);
}

/**
 * Pinch-zoom handler (touch).
 * @param {Element} shell
 */
function setupPinchZoom(shell) {
    const state = getState(shell);
    const viewArea = state.viewArea;
    const pageContainer = state.pageContainer;
    if (!viewArea || !pageContainer) return;

    let initialDistance = 0;
    let initialZoomIndex = 0;

    viewArea.addEventListener(
        'touchstart',
        (e) => {
            if (e.touches.length !== 2) return;
            initialDistance = Math.hypot(
                e.touches[1].clientX - e.touches[0].clientX,
                e.touches[1].clientY - e.touches[0].clientY
            );
            initialZoomIndex = state.zoomIndex;
        },
        { passive: true }
    );

    viewArea.addEventListener(
        'touchmove',
        (e) => {
            if (e.touches.length !== 2 || initialDistance === 0) return;
            e.preventDefault();
            const dist = Math.hypot(
                e.touches[1].clientX - e.touches[0].clientX,
                e.touches[1].clientY - e.touches[0].clientY
            );
            const delta = (dist - initialDistance) / 100;
            let next = Math.round(initialZoomIndex + delta);
            next = Math.max(0, Math.min(ZOOM_STEPS.length - 1, next));
            if (next !== state.zoomIndex) {
                state.zoomIndex = next;
                applyZoom(pageContainer, ZOOM_STEPS[state.zoomIndex]);
            }
        },
        { passive: false }
    );

    viewArea.addEventListener('touchend', () => {
        initialDistance = 0;
    });
}

/**
 * Disable context menu on viewer area.
 * @param {Element} shell
 */
function disableContextMenu(shell) {
    const viewArea = shell.querySelector('.secure-viewer__view-area');
    if (viewArea) {
        viewArea.addEventListener('contextmenu', (e) => e.preventDefault());
    }
}

/**
 * Compute PhotoSwipe opening origin from the current preview image (page 1).
 * @param {Element} shell
 * @returns {{x:number,y:number,w:number}|null}
 */
function getThumbBoundsFromPreview(shell) {
    const img = shell.querySelector('img.secure-viewer__page-image');
    if (!img) return null;
    const rect = img.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return { x: rect.left, y: rect.top, w: rect.width };
}

/**
 * Create and initialize a PhotoSwipe lightbox for this secure viewer shell.
 * @param {Element} shell
 */
function setupPhotoSwipe(shell) {
    const state = getState(shell);
    if (state.photoSwipeLightbox) return;
    if (!Number.isFinite(state.totalPages) || state.totalPages < 1) return;

    const pswpModule = PhotoSwipe?.default || PhotoSwipe;

    const previewImg = shell.querySelector('img.secure-viewer__page-image');
    const seedW = previewImg?.naturalWidth || previewImg?.width || 2000;
    const seedH = previewImg?.naturalHeight || previewImg?.height || 2000;

    // PhotoSwipe expects a gallery element with children anchors.
    const galleryEl = document.createElement('div');
    galleryEl.className = 'pswp-gallery secure-viewer__pswp-gallery';
    galleryEl.setAttribute('aria-hidden', 'true');
    galleryEl.id = `secure-viewer-pswp-${Math.random().toString(36).slice(2)}`;

    for (let i = 1; i <= state.totalPages; i++) {
        const a = document.createElement('a');
        a.href = 'about:blank';
        a.target = '_blank';
        a.dataset.page = String(i);
        a.dataset.pswpSrc = 'about:blank';
        a.dataset.pswpWidth = String(seedW);
        a.dataset.pswpHeight = String(seedH);
        galleryEl.appendChild(a);
    }
    shell.appendChild(galleryEl);

    const lightbox = new PhotoSwipeLightbox({
        gallery: `#${galleryEl.id}`,
        children: 'a',
        pswpModule,
        showHideAnimationType: 'zoom',
        padding: { top: 0, bottom: 0, left: 0, right: 0 },
        closeOnVerticalDrag: false,
        pinchToClose: false,
        clickToCloseNonZoomable: false,
        // Hide built-in lens zoom button (we provide +/− instead).
        zoom: false,
        // We'll implement our own click-to-zoom logic via the `imageClickAction` event.
        imageClickAction: 'none',
        tapAction: 'toggle-controls',
        doubleTapAction: 'zoom',
        // Keep UI (paging/zoom icons) visible all the time.
        idleTimeout: 0,
        // Hide page behind the overlay.
        bgOpacity: 1,
        // Persist zoom between pages based on selected mode.
        initialZoomLevel: (zoomLevels) => {
            if (state.photoSwipeZoomMode === 'custom') {
                const saved = state.photoSwipeLastCustomZoomLevel;
                const fit = zoomLevels?.fit;
                const max = zoomLevels?.max;
                if (typeof saved === 'number' && typeof fit === 'number' && typeof max === 'number') {
                    return Math.max(fit, Math.min(saved, max));
                }
            }
            return zoomLevels?.fit ?? 'fit';
        },
        secondaryZoomLevel: 1.25,
        maxZoomLevel: 3,
        mainClass: 'pswp--secure-viewer',
    });

    // Styling for custom zoom UI (modes, readout, page shadow).
    // Use a stable <style> tag so updates apply without full reload.
    {
        const STYLE_ID = 'secure-viewer-pswp-style';
        let style = document.getElementById(STYLE_ID);
        if (!style) {
            style = document.createElement('style');
            style.id = STYLE_ID;
            document.head.appendChild(style);
        }
        style.textContent = `
          .pswp__sv-zoom-level {
            /* Match PhotoSwipe page counter look (no backdrop pill) */
            height: 30px;
            margin-top: 15px;
            margin-inline-start: 20px;
            font-size: 14px;
            line-height: 30px;
            color: var(--pswp-icon-color);
            text-shadow: 1px 1px 3px var(--pswp-icon-color-secondary);
            opacity: 0.85;
            user-select: none;
            display: block;
            padding: 0;
            min-width: 0;
            border-radius: 0;
            background: transparent;
            backdrop-filter: none;
          }
          .pswp--secure-viewer .pswp__top-bar {
            gap: 2px;
            padding-left: 10px;
            padding-right: 10px;
          }
          .pswp--secure-viewer .pswp__bg {
            opacity: 1 !important;
          }
          .pswp--secure-viewer .pswp__button--sv-fit,
          .pswp--secure-viewer .pswp__button--sv-custom,
          .pswp--secure-viewer .pswp__button--sv-width,
          .pswp--secure-viewer .pswp__button--sv-plus,
          .pswp--secure-viewer .pswp__button--sv-minus {
            width: 40px;
            height: 40px;
            padding: 0;
            margin-top: 10px;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: rgba(0,0,0,0.35);
            backdrop-filter: blur(6px);
            /* Inactive styling (requested): light grey icon + border */
            border: 1px solid rgba(255,255,255,0.35);
            color: rgba(255,255,255,0.72);
            backdrop-filter: blur(6px);
          }
          .pswp--secure-viewer .pswp__button--sv-custom .pswp__icn,
          .pswp--secure-viewer .pswp__button--sv-fit .pswp__icn,
          .pswp--secure-viewer .pswp__button--sv-width .pswp__icn,
          .pswp--secure-viewer .pswp__button--sv-plus .pswp__icn,
          .pswp--secure-viewer .pswp__button--sv-minus .pswp__icn {
            color: currentColor;
            display: block;
            margin: 0;
            position: static !important;
            left: auto !important;
            top: auto !important;
            transform: none !important;
          }
          /* Default (inactive) icon weight: lighter/cleaner */
          .pswp--secure-viewer .pswp__button--sv-fit .pswp__icn path,
          .pswp--secure-viewer .pswp__button--sv-fit .pswp__icn circle,
          .pswp--secure-viewer .pswp__button--sv-custom .pswp__icn path,
          .pswp--secure-viewer .pswp__button--sv-custom .pswp__icn circle,
          .pswp--secure-viewer .pswp__button--sv-width .pswp__icn path,
          .pswp--secure-viewer .pswp__button--sv-width .pswp__icn circle,
          .pswp--secure-viewer .pswp__button--sv-plus .pswp__icn path,
          .pswp--secure-viewer .pswp__button--sv-plus .pswp__icn circle,
          .pswp--secure-viewer .pswp__button--sv-minus .pswp__icn path,
          .pswp--secure-viewer .pswp__button--sv-minus .pswp__icn circle {
            stroke-width: 1.6 !important;
            opacity: 0.88;
          }
          .pswp--secure-viewer .pswp__button--sv-fit:hover {
            background: rgba(0,0,0,0.1);
          }
          .pswp--secure-viewer .pswp__button--sv-custom:hover,
          .pswp--secure-viewer .pswp__button--sv-width:hover,
          .pswp--secure-viewer .pswp__button--sv-plus:hover,
          .pswp--secure-viewer .pswp__button--sv-minus:hover {
            background: rgba(0,0,0,0.5);
          }
          /* Active state (requested): bright white button, like inactive style of others */
          .pswp--secure-viewer .pswp__button.sv-is-active {
            background: rgba(255,255,255,0.92) !important;
            border-color: rgba(255,255,255,0.98) !important;
            box-shadow:
              0 0 0 2px rgba(255,255,255,0.75) inset,
              0 14px 30px rgba(0,0,0,0.38);
          }
          .pswp--secure-viewer .pswp__button.sv-is-active .pswp__icn {
            color: rgba(0,0,0,0.85);
            filter: none;
          }
          /* Active icon weight: use the "bold" stroke */
          .pswp--secure-viewer .pswp__button.sv-is-active .pswp__icn path,
          .pswp--secure-viewer .pswp__button.sv-is-active .pswp__icn circle {
            stroke-width: 2.2 !important;
            opacity: 1;
          }
          .pswp--secure-viewer .pswp__img {
            background: #fff;
            box-shadow: 0 18px 60px rgba(0,0,0,0.35);
            border-radius: 2px;
          }
          .pswp__button.is-hidden {
            display: none !important;
          }
          .pswp__sv-zoom-level.is-hidden {
            display: none !important;
          }
        `;
    }

    // Animate zoom-from origin based on the preview image.
    lightbox.addFilter('thumbBounds', (thumbBounds) => {
        return getThumbBoundsFromPreview(shell) || thumbBounds;
    });

    const updateZoomModeUi = () => {
        const ui = state.photoSwipeUiEls;
        if (!ui) return;
        ui.fit?.classList.toggle('sv-is-active', state.photoSwipeZoomMode === 'fit');
        ui.width?.classList.toggle('sv-is-active', state.photoSwipeZoomMode === 'width');
    };

    const applyZoomModeToCurrentSlide = () => {
        const pswp = window.pswp;
        const slide = pswp?.currSlide;
        if (!pswp?.isOpen || !slide || typeof slide.zoomTo !== 'function') return;
        const fit = slide?.zoomLevels?.fit;
        const max = slide?.zoomLevels?.max;
        if (typeof fit !== 'number' || typeof max !== 'number') return;

        if (state.photoSwipeZoomMode === 'fit') {
            state.photoSwipeSuppressAutoCustomUntil = Date.now() + 250;
            slide.zoomTo(fit, false, 0);
            return;
        }

        if (state.photoSwipeZoomMode === 'width') {
            const panAreaW = slide?.panAreaSize?.x;
            const imgW = slide?.width;
            if (typeof panAreaW !== 'number' || typeof imgW !== 'number' || imgW <= 0) return;
            const widthZoom = Math.max(fit, Math.min(panAreaW / imgW, max));
            state.photoSwipeSuppressAutoCustomUntil = Date.now() + 250;
            slide.zoomTo(widthZoom, false, 0);
            window.setTimeout(() => {
                try {
                    const topY = slide?.bounds?.min?.y;
                    const centerX = slide?.bounds?.center?.x;
                    if (typeof topY === 'number' && typeof centerX === 'number') {
                        slide.panTo(centerX, topY);
                    }
                } catch (_) {}
            }, 0);
            return;
        }

        if (state.photoSwipeZoomMode === 'custom') {
            const saved = state.photoSwipeLastCustomZoomLevel;
            if (typeof saved === 'number') {
                state.photoSwipeSuppressAutoCustomUntil = Date.now() + 250;
                slide.zoomTo(Math.max(fit, Math.min(saved, max)), false, 0);
            }
        }
    };

    const setZoomMode = (mode) => {
        if (mode !== 'fit' && mode !== 'width' && mode !== 'custom') return;
        // Explicit modes only; no "deactivate" toggle (less confusing).
        state.photoSwipeZoomMode = mode;
        updateZoomModeUi();
        applyZoomModeToCurrentSlide();
    };

    // After open animation, lock UI to fit/contain and refresh active button (avoids early zoomPanUpdate → custom).
    lightbox.on('openingAnimationEnd', () => {
        state.photoSwipeUiReady = true;
        state.photoSwipeZoomMode = 'fit';
        state.photoSwipeSuppressAutoCustomUntil = Date.now() + 600;
        updateZoomModeUi();
    });

    // Click-to-zoom toggle:
    // - 1st click: fit-width + pan top
    // - 2nd click: back to fit
    lightbox.on('imageClickAction', (e) => {
        if (typeof e?.preventDefault === 'function') e.preventDefault();

        const pswp = e?.pswp || window.pswp;
        const slide = pswp?.currSlide;
        if (!pswp?.isOpen || !slide || typeof slide.zoomTo !== 'function') return;

        const point = e?.point || pswp.getViewportCenterPoint?.() || false;
        const fit = slide?.zoomLevels?.fit;
        const max = slide?.zoomLevels?.max ?? pswp?.options?.maxZoomLevel;
        const mode = PSWP_CLICK_ZOOM_MODE.get(slide) === true;

        try {
            pswp?.ui?.showControls?.();
        } catch (_) {}

        if (!mode) {
            const panAreaW = slide?.panAreaSize?.x;
            const imgW = slide?.width;
            if (typeof panAreaW === 'number' && typeof imgW === 'number' && imgW > 0) {
                let widthZoom = panAreaW / imgW;
                if (typeof max === 'number') widthZoom = Math.min(widthZoom, max);
                slide.zoomTo(widthZoom, point, 0);
                PSWP_CLICK_ZOOM_MODE.set(slide, true);
                window.setTimeout(() => {
                    try {
                        const topY = slide?.bounds?.min?.y;
                        const centerX = slide?.bounds?.center?.x;
                        if (typeof topY === 'number' && typeof centerX === 'number') {
                            slide.panTo(centerX, topY);
                        }
                        pswp?.ui?.showControls?.();
                    } catch (_) {}
                }, 0);
                return;
            }
        } else if (typeof fit === 'number') {
            slide.zoomTo(fit, point, 0);
            PSWP_CLICK_ZOOM_MODE.set(slide, false);
            window.setTimeout(() => {
                try {
                    pswp?.ui?.showControls?.();
                } catch (_) {}
            }, 0);
            return;
        }
    });

    // Track custom zoom level (for conservation on page change).
    // Note: the most reliable hook is the PhotoSwipe core event in uiRegister below,
    // because it fires for wheel/pinch and button zoom.

    // Apply the selected mode on page change and after load.
    lightbox.on('change', () => {
        const attempt = (triesLeft) => {
            applyZoomModeToCurrentSlide();
            const pswp = window.pswp;
            const slide = pswp?.currSlide;
            const ready = typeof slide?.zoomLevels?.fit === 'number' && typeof slide?.zoomLevels?.max === 'number';
            if (!ready && triesLeft > 0) window.setTimeout(() => attempt(triesLeft - 1), 80);
        };
        attempt(20);
    });

    lightbox.on('loadComplete', () => {
        if (state.photoSwipeZoomMode === 'width') applyZoomModeToCurrentSlide();
    });

    // Register zoom mode buttons (+/- for custom) in the PhotoSwipe top bar.
    lightbox.on('uiRegister', () => {
        const pswp = window.pswp;
        if (!pswp?.ui?.registerElement) return;

        const forceOpaqueBackdrop = () => {
            const bg = document.querySelector('.pswp.pswp--secure-viewer .pswp__bg');
            if (bg) bg.style.opacity = '1';
        };

        const updateZoomLevelUi = () => {
            const el = state.photoSwipeUiEls?.zoomLevel;
            const slide = window.pswp?.currSlide;
            const z = slide?.currZoomLevel;
            if (!el) return;
            if (typeof z !== 'number') {
                el.textContent = '';
                el.classList.add('is-hidden');
                return;
            }
            el.classList.remove('is-hidden');
            el.textContent = `${Math.round(z * 100)}%`;
        };

        const mkSvg = (inner) => ({
            isCustomSVG: true,
            size: 32,
            inner,
        });

        const register = (key, label, title, order, onClick) => {
            pswp.ui.registerElement({
                name: `sv-${key}`,
                className: `pswp__button--sv-${key}`,
                title,
                ariaLabel: title,
                order,
                isButton: true,
                html: mkSvg(label),
                onInit: (el) => {
                    state.photoSwipeUiEls = state.photoSwipeUiEls || {};
                    state.photoSwipeUiEls[key] = el;
                    el.addEventListener('click', (ev) => {
                        ev.preventDefault();
                        onClick();
                    });
                    updateZoomModeUi();
                    updateZoomLevelUi();
                },
            });
        };

        // Persist custom zoom and auto-switch to custom when user manually zooms.
        pswp.on('zoomPanUpdate', () => {
            try {
                const slide = pswp.currSlide;
                const z = slide?.currZoomLevel;
                const fit = slide?.zoomLevels?.fit;
                if (typeof z !== 'number' || typeof fit !== 'number') return;

                updateZoomLevelUi();

                // Ignore zoom updates caused by our own mode application
                // (open/fit/width/custom apply) so we don't immediately switch to custom.
                if (Date.now() < (state.photoSwipeSuppressAutoCustomUntil || 0)) return;
                if (state.photoSwipeUiReady === false) return;
                // Width mode is explicit; don't auto-switch to custom while active.
                if (state.photoSwipeZoomMode === 'width') return;

                const eps = 0.01;
                const isManualZoom = Math.abs(z - fit) > eps;
                if (isManualZoom) {
                    // Any manual zoom action should switch to custom mode,
                    // which deactivates width mode and allows conserving zoom.
                    if (state.photoSwipeZoomMode !== 'custom') {
                        state.photoSwipeZoomMode = 'custom';
                        updateZoomModeUi();
                    }
                    state.photoSwipeLastCustomZoomLevel = z;
                }
            } catch (_) {}
        });

        // Hide page behind (backdrop opacity sometimes remains at default).
        forceOpaqueBackdrop();
        pswp.on('change', forceOpaqueBackdrop);
        pswp.on('loadComplete', forceOpaqueBackdrop);

        pswp.ui.registerElement({
            name: 'sv-zoom-level',
            className: 'pswp__sv-zoom-level',
            order: 6,
            isButton: false,
            tagName: 'div',
            appendTo: 'bar',
            onInit: (el) => {
                state.photoSwipeUiEls = state.photoSwipeUiEls || {};
                state.photoSwipeUiEls.zoomLevel = el;
                el.setAttribute('aria-live', 'polite');
                el.setAttribute('aria-atomic', 'true');
                updateZoomLevelUi();

                pswp.on('change', () => window.setTimeout(updateZoomLevelUi, 0));
                pswp.on('loadComplete', () => window.setTimeout(updateZoomLevelUi, 0));
            },
        });

        // Stable layout: Fit (contain) / Width / + / -
        register(
            'fit',
            `<rect x="10.5" y="8.5" width="11" height="15" rx="1.6" fill="none" stroke="currentColor" stroke-width="2.2"/>
             <path d="M14 13h4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
             <path d="M14 19h4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
             <path d="M13.2 15.8l-2.2-2.2" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
             <path d="M18.8 15.8l2.2-2.2" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>`,
            'Fit (contain)',
            7,
            () => setZoomMode('fit')
        );
        register(
            'width',
            `<path d="M11 8h8l3 3v13H11z" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"/>
             <path d="M19 8v4h4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round"/>
             <path d="M9 14v8" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
             <path d="M25 14v8" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
             <path d="M14 18h6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
             <path d="M15.4 16.6L13 18l2.4 1.4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
             <path d="M18.6 16.6L21 18l-2.4 1.4" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`,
            'Zoom to width',
            8,
            () => setZoomMode('width')
        );

        const stepCustom = (dir) => {
            const slide = pswp.currSlide;
            if (!pswp.isOpen || !slide || typeof slide.zoomTo !== 'function') return;
            const fit = slide?.zoomLevels?.fit;
            const max = slide?.zoomLevels?.max;
            const curr = slide?.currZoomLevel;
            if (![fit, max, curr].every((n) => typeof n === 'number')) return;
            const factor = dir > 0 ? 1.25 : 0.8;
            const next = Math.max(fit, Math.min(curr * factor, max));
            // +/- always switches to custom and deactivates other modes (W).
            state.photoSwipeZoomMode = 'custom';
            state.photoSwipeLastCustomZoomLevel = next;
            state.photoSwipeSuppressAutoCustomUntil = Date.now() + 250;
            slide.zoomTo(next, false, 0);
            updateZoomModeUi();
        };

        register(
            'plus',
            `<circle cx="14" cy="14" r="7" fill="none" stroke="currentColor" stroke-width="2.2"/>
             <path d="M19.5 19.5L24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
             <path d="M14 11v6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
             <path d="M11 14h6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>`,
            'Zoom in (switches to custom)',
            9,
            () => stepCustom(1)
        );
        register(
            'minus',
            `<circle cx="14" cy="14" r="7" fill="none" stroke="currentColor" stroke-width="2.2"/>
             <path d="M19.5 19.5L24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
             <path d="M11 14h6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>`,
            'Zoom out (switches to custom)',
            10,
            () => stepCustom(-1)
        );
    });

    // Custom secure loader: fetch images via our API and set src on the
    // internal PhotoSwipe <img> element (do not replace content.element).
    lightbox.on('contentLoadImage', (e) => {
        const content = e?.content;
        const element = content?.element;
        if (!content || !element) return;

        const pageStr = content?.data?.element?.dataset?.page || '';
        const page = parseInt(pageStr, 10);
        if (!Number.isFinite(page) || page < 1) return;

        // PhotoSwipe will otherwise try to load `src` from anchor href.
        if (typeof e.preventDefault === 'function') e.preventDefault();

        const finalizeFromBlobUrl = (blobUrl) => {
            state.photoSwipeBlobUrls.set(page, blobUrl);

            element.src = blobUrl;
            element.classList.add('pswp__img');
            element.classList.remove('pswp__img--placeholder');
            element.style.maxWidth = 'none';
            element.style.maxHeight = 'none';

            const done = () => {
                try {
                    content.onLoaded();
                } catch (_) {
                    try {
                        content.onError();
                    } catch (_) {}
                }
            };

            if (element.complete) {
                done();
            } else {
                element.onload = () => done();
                element.onerror = () => content.onError();
            }
        };

        const cachedUrl = state.photoSwipeBlobUrls.get(page);
        if (cachedUrl) {
            finalizeFromBlobUrl(cachedUrl);
            return;
        }

        let p = state.photoSwipePagePromises.get(page);
        if (!p) {
            p = loadPageImage(shell, page)
                .then(({ blobUrl }) => blobUrl);
            state.photoSwipePagePromises.set(page, p);
        }

        p.then((blobUrl) => finalizeFromBlobUrl(blobUrl)).catch(() => content.onError());
    });

    // Fit enforcement is handled in `openPhotoSwipe` with a post-open retry loop.

    lightbox.on('close', () => {
        state.photoSwipeUiReady = false;
        // Revoke all blob URLs that were created for this viewer session.
        for (const url of state.photoSwipeBlobUrls.values()) {
            try {
                URL.revokeObjectURL(url);
            } catch (_) {}
        }
        state.photoSwipeBlobUrls.clear();
        state.photoSwipePagePromises.clear();
    });

    lightbox.init();
    state.photoSwipeLightbox = lightbox;
    state.photoSwipeGalleryEl = galleryEl;

    // Click-to-zoom is handled via PhotoSwipe's `imageClickAction` event.
}

/**
 * Open PhotoSwipe at a specific page.
 * @param {Element} shell
 * @param {number} pageNumber 1-based
 */
async function openPhotoSwipe(shell, pageNumber) {
    await ensurePreviewImageDimensions(shell);
    setupPhotoSwipe(shell);
    const state = getState(shell);
    if (!state.photoSwipeLightbox) return;
    state.photoSwipeUiReady = false;
    state.photoSwipeZoomMode = 'fit';
    state.photoSwipeSuppressAutoCustomUntil = Date.now() + 800;
    const idx = Math.max(0, (pageNumber || 1) - 1);
    state.photoSwipeLightbox.loadAndOpen(idx);
    forcePhotoSwipeFitZoomAfterOpen();

    function forcePhotoSwipeFitZoomAfterOpen() {
        const attemptApplyFit = (remainingTries) => {
            try {
                const ps = window.pswp;
                const active = ps?.currSlide;
                if (!ps?.isOpen || ps?.currIndex !== 0 || !active) return;
                const fit = active?.zoomLevels?.fit;
                if (typeof fit === 'number') {
                    active.zoomTo(fit, false, 0);
                    return;
                }
                if (remainingTries <= 0) return;
                window.setTimeout(() => attemptApplyFit(remainingTries - 1), 150);
            } catch (_) {}
        };

        // Fit can be missing for a short period depending on load timing.
        attemptApplyFit(12);
    }
}

/**
 * Wait until the preview <img> has real dimensions (naturalWidth/naturalHeight)
 * so PhotoSwipe can compute the correct initial "fit" zoom.
 * @param {Element} shell
 * @param {number} timeoutMs
 */
async function ensurePreviewImageDimensions(shell, timeoutMs = 5000) {
    const img = shell.querySelector('img.secure-viewer__page-image');
    if (!img) return;
    const hasDims = (img.naturalWidth || 0) > 0 && (img.naturalHeight || 0) > 0;
    if (hasDims) return;

    if (img.complete) return; // complete but still no dims - bail out

    await new Promise((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
        };
        img.addEventListener('load', finish, { once: true });
        img.addEventListener('error', finish, { once: true });
        window.setTimeout(finish, timeoutMs);
    });
}

/**
 * @param {Element} shell
 */
function runSecureViewer(shell) {
    const totalPages = parseInt(shell.getAttribute('data-secure-viewer-total-pages'), 10);
    if (!Number.isFinite(totalPages) || totalPages < 1) return;

    const state = getState(shell);
    state.totalPages = totalPages;

    disableContextMenu(shell);

    // PhotoSwipe open button (normal view reduced to a preview).
    const openBtn = shell.querySelector('.secure-viewer__btn--open-photoswipe');
    if (openBtn) {
        openBtn.addEventListener('click', async () => {
            openBtn.disabled = true;
            try {
                await openPhotoSwipe(shell, state.currentPage || 1);
            } finally {
                openBtn.disabled = false;
            }
        });
    }

    goToPage(shell, 1);
}

function init() {
    removeTokenFromUrl();
    const shells = document.querySelectorAll('.secure-viewer__shell[data-secure-viewer-total-pages]');
    shells.forEach((shell) => {
        const totalPages = parseInt(shell.getAttribute('data-secure-viewer-total-pages'), 10);
        if (!Number.isFinite(totalPages) || totalPages < 1) return;
        if (shell.dataset.secureViewerInitialized) return;
        shell.dataset.secureViewerInitialized = '1';
        runSecureViewer(shell);
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
